import {isOptionalTimeZone} from './date-time-metadata.js';
import { isVenueProjection } from './venue-responses.js';

export class VenueRequestResponseContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} response: ${detail}`);
    this.name = 'VenueRequestResponseContractError';
    this.code = 'INVALID_API_RESPONSE';
  }
}

const statuses = new Set(['pending', 'approved', 'declined', 'cancelled', 'superseded']);
const kinds = new Set(['residence', 'communityVenueBooking']);
const venueTypes = new Set(['Residence', 'Online', 'CommunityVenue']);
const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isText = value => typeof value === 'string';
const isReference = value => isText(value) && value.length > 0 || isRecord(value) && isText(value._id) && value._id.length > 0;
const optionalReference = value => value === undefined || value === null || isReference(value);
const optionalText = value => value === undefined || value === null || isText(value);
const optionalBoolean = value => value === undefined || typeof value === 'boolean';
const optionalDate = value => value == null || typeof value === 'string' && Number.isFinite(Date.parse(value));
const optionalGroupDetails = value => value == null || isRecord(value)
  && ['day', 'time', 'frequency'].every(field => optionalText(value[field]));
const venueList = value => value === undefined || Array.isArray(value) && value.every(item => isReference(item) && (!isRecord(item) || isVenueProjection(item)));
const optionalAddress = value => value == null || isRecord(value)
  && ['streetAddress', 'suburb', 'city', 'state', 'postalCode'].every(field => optionalText(value[field]));

const populatedReferenceIsValid = (value, fields) => !isRecord(value) || fields.every(([key, check]) => check(value[key]));
const optionalImage = value => value == null || isText(value) || isRecord(value) && optionalText(value.url);
const optionalImages = value => value == null || Array.isArray(value) && value.every(optionalImage);
const activityProjectionIsValid = value => populatedReferenceIsValid(value, [
  ['title', optionalText], ['imageUrl', optionalText], ['image', optionalImage], ['images', optionalImages], ['photoUrl', optionalText],
  ['activityType', optionalReference], ['community', optionalReference], ['groupDetails', optionalGroupDetails], ['approvedCommunityVenues', venueList],
]) && (!isRecord(value?.activityType) || optionalText(value.activityType.name));
const secretFree = value => !isRecord(value) || ['password', 'passwordHash', 'refreshToken', 'emailVerificationCode', 'resetPasswordToken', 'resetPasswordExpires', 'verificationToken', 'emailVerificationToken', 'emailVerificationExpires']
  .every(field => value[field] === undefined) && value.verificationCode == null;

function hasValidRelationships(request) {
  return ['community', 'requestedByUser', 'targetHousehold', 'targetVenue', 'activity', 'session', 'event', 'approverUser', 'decidedByUser', 'supersededByUser']
    .every(key => optionalReference(request[key]))
    && ['requestedByUser', 'approverUser', 'decidedByUser', 'supersededByUser'].every(field => secretFree(request[field]))
    && ['requestedByUser', 'approverUser', 'decidedByUser', 'supersededByUser'].every(field => populatedReferenceIsValid(request[field],
      [['firstName', optionalText], ['lastName', optionalText], ['email', optionalText], ['profilePicture', optionalText], ['community', optionalReference]]))
    && populatedReferenceIsValid(request.targetHousehold, [['displayName', optionalText], ['address', optionalText], ['community', optionalReference], ['venueApprover', optionalReference], ['acceptVenueRequest', optionalBoolean], ['allowRepeatResidenceRequests', optionalBoolean]])
    && (!isRecord(request.targetVenue) || isVenueProjection(request.targetVenue))
    && populatedReferenceIsValid(request.targetVenue, [['name', optionalText], ['type', value => value === undefined || venueTypes.has(value)], ['availableForRequest', optionalBoolean], ['address', optionalAddress], ['household', optionalReference], ['community', optionalReference], ['onlineLink', optionalText]])
    && activityProjectionIsValid(request.activity)
    && populatedReferenceIsValid(request.event, [['title', optionalText], ['eventType', optionalText], ['imageUrl', optionalText], ['onlineLink', optionalText], ['address', optionalAddress], ['date', optionalDate], ['startTime', optionalDate], ['endTime', optionalDate], ['community', optionalReference], ['venues', venueList]])
    && populatedReferenceIsValid(request.session, [['date', optionalDate], ['imageUrl', optionalText], ['image', optionalImage], ['onlineLink', optionalText], ['address', optionalAddress], ['status', value => value === undefined || ['Scheduled', 'Pending', 'Confirmed', 'Completed', 'Cancelled', 'Expired'].includes(value)], ['activity', optionalReference], ['community', optionalReference], ['venues', venueList]])
    && activityProjectionIsValid(request.session?.activity)
    && populatedReferenceIsValid(request.event, [['image', optionalImage]]);
}

export function readVenueRequestRecordResponse(payload, operation = 'venue request', expectedStatus = null) {
  if (!isRecord(payload)
    || typeof payload._id !== 'string' || !payload._id
    || !statuses.has(payload.status)
    || (expectedStatus && payload.status !== expectedStatus)
    || (payload.kind !== undefined && !kinds.has(payload.kind))
    || (payload.approvalMode !== undefined && payload.approvalMode !== null && !['once', 'ongoing'].includes(payload.approvalMode))
    || (payload.bookingStartsAt != null || payload.bookingEndsAt != null)
      && (!payload.bookingStartsAt || !payload.bookingEndsAt || Date.parse(payload.bookingEndsAt) <= Date.parse(payload.bookingStartsAt))
    || !isOptionalTimeZone(payload.bookingTimeZone)
    || !optionalBoolean(payload.requiresAssemblyApproval)
    || !optionalBoolean(payload.isRepeatRequest)
    || !optionalText(payload.requesterNote)
    || !optionalText(payload.decisionNote)
    || !optionalText(payload.supersededReason)
    || !['bookingStartsAt', 'bookingEndsAt', 'decidedAt', 'supersededAt', 'createdAt', 'updatedAt'].every(field => optionalDate(payload[field]))
    || !hasValidRelationships(payload)) {
    throw new VenueRequestResponseContractError(operation, 'expected a direct venue request with _id and status.');
  }
  return payload;
}

export function readVenueRequestInboxResponse(payload) {
  if (!Array.isArray(payload)) {
    throw new VenueRequestResponseContractError('venue request inbox', 'expected a direct array.');
  }
  payload.forEach((request) => readVenueRequestRecordResponse(request, 'venue request inbox item', 'pending'));
  return payload;
}

/** Residence selection can assign the actor's own venue immediately. */
export function readResidenceVenueRequestResponse(payload, operation = 'residence venue request') {
  if (isRecord(payload) && payload.status === 'assigned') {
    if (payload.action !== 'ASSIGN' || !isText(payload.venueId) || !payload.venueId) {
      throw new VenueRequestResponseContractError(operation, 'expected an explicit assigned venue acknowledgement.');
    }
    return payload;
  }
  return readVenueRequestRecordResponse(payload, operation, 'pending');
}

export function readGuardianVenueRequestsResponse(payload) {
  if (!payload || payload.success !== true || !Array.isArray(payload.data)) {
    throw new VenueRequestResponseContractError('guardian venue requests', 'expected { success: true, data: VenueRequest[] }.');
  }
  payload.data.forEach((request) => readVenueRequestRecordResponse(request, 'guardian venue request'));
  if (new Set(payload.data.map(row => row._id.toLowerCase())).size !== payload.data.length) throw new VenueRequestResponseContractError('guardian venue requests', 'expected unique request IDs.');
  return payload;
}

export function readGuardianVenueRequestHistoryResponse(payload) {
  readGuardianVenueRequestsResponse(payload);
  if (!Number.isInteger(payload.limit) || payload.limit < 1 || payload.limit > 100 || payload.data.length > payload.limit) {
    throw new VenueRequestResponseContractError('guardian venue request history', 'expected a positive limit.');
  }
  return payload;
}

export function readGuardianVenueRequestMutationResponse(payload, operation = 'guardian venue request decision', expectedStatus = null, requestedId = null) {
  if (!payload || payload.success !== true) {
    throw new VenueRequestResponseContractError(operation, 'expected { success: true, data: VenueRequest }.');
  }
  readVenueRequestRecordResponse(payload.data, operation, expectedStatus);
  if (requestedId && payload.data._id.toLowerCase() !== requestedId.toLowerCase()) throw new VenueRequestResponseContractError(operation, 'expected the requested venue decision.');
  return payload;
}
